import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import ApplicationsAction, {
   isFetchingApplication,
   patchApplicationRequest,
   isFetchingApplicationSuccess
} from 'pgdb-data-layer/lib/Redux/ApplicationsRedux';
import CourseActions, {
   isFetchingActiveEndorsementCourse,
   getActiveEndorsementCourseData,
   isFetchingHowIOurlSSO
} from 'pgdb-data-layer/lib/Redux/CoursesRedux';
import { APPLICATION_REGISTRATION_ON_HOLD_PENDING_PAYMENT, APPLICATION_STATUS_PENDING_TRAINING } from 'pgdb-data-layer/lib/Constants/Contact';
import Loading from '../../Components/Loading';
import StepIndicator from '../../Components/StepIndicator';
import { getToken } from '../../Session/SessionRedux';
import Navigator from '../../Navigation/Navigator';
import './Endorsements/ApplyEndorsement.scss';
import { STEPS_ENDORSEMENT_FORM } from './EndorsementTypePage';
import EndorsementTimeLimitNotice from './Endorsements/EndorsementTimeLimitNotice';
import { PASS_CODE } from 'pgdb-data-layer/lib/Constants/Course';

const EndorsementTrainingPage = ({ token, isLoading, patchApplication, getApplicationPayload, activeCourse, resetUrlHowIO, getActiveEndorsementCourse, endorsement, callGetURLHowIO, urlHowIO, updatePatchSuccess, isRenewal }) => {
   const { applicationId } = useParams();
   const [canContinue, setCanContinue] = useState(false);
   const [confirmedSelf, setConfirmedSelf] = useState(false);
   const backfillAttempted = React.useRef(false);

   useEffect(() => {
      getActiveEndorsementCourse(token);
      if(applicationId) {
         getApplicationPayload(token, applicationId);
      }
   }, [token,applicationId]);

   useEffect(() => {
      const currentEndorsement = endorsement.find(e => e.applicationId === parseInt(applicationId));
      if (!currentEndorsement) return;

      if (currentEndorsement.endorsementTrainingCertifiedTimestamp || currentEndorsement.endorsementTrainingOverrideTimestamp) {
         setConfirmedSelf(true);
      } else if (currentEndorsement.applicationStatusCode === APPLICATION_REGISTRATION_ON_HOLD_PENDING_PAYMENT && !backfillAttempted.current) {
         backfillAttempted.current = true;
         patchApplication(token, applicationId, { ApplicationStatusCode: APPLICATION_REGISTRATION_ON_HOLD_PENDING_PAYMENT });
      }
   }, [endorsement, applicationId]);

   useEffect(() => {
      if (activeCourse && activeCourse.course && activeCourse.result) {
         const passedTraining = activeCourse.result.some(
            r => r.courseId === activeCourse.course.courseId && r.passFailCode === PASS_CODE
         );
         
         const currentEndorsement = endorsement.find(e => e.applicationId === parseInt(applicationId)); 
         if (currentEndorsement && passedTraining && currentEndorsement.applicationStatusCode === APPLICATION_STATUS_PENDING_TRAINING) { 
            patchApplication(token, applicationId, { ApplicationStatusCode: APPLICATION_REGISTRATION_ON_HOLD_PENDING_PAYMENT });
         }
         setCanContinue(passedTraining && currentEndorsement && currentEndorsement.applicationStatusCode === APPLICATION_REGISTRATION_ON_HOLD_PENDING_PAYMENT);
      }
   }, [activeCourse, endorsement, updatePatchSuccess]);

   const onSaveAndNext = () => {
      const currentEndorsement = endorsement.find(e => e.applicationId === parseInt(applicationId));
      if(currentEndorsement && currentEndorsement.applicationStatusCode === APPLICATION_REGISTRATION_ON_HOLD_PENDING_PAYMENT) {
         patchApplication(token, applicationId, { ApplicationStatusCode: APPLICATION_REGISTRATION_ON_HOLD_PENDING_PAYMENT });
         if (isRenewal) {
            Navigator.toRenewEndorsementPayment(applicationId);
         } else {
            Navigator.toEndorsementPayment(applicationId);
         }
      }
   };

   const onBack = () => {
      if (isRenewal) {
         Navigator.toRenewEndorsementDetails(applicationId);
      } else {
         Navigator.toEndorsementDetails(applicationId);
      }
   };


   const getHowIOUrlCourse = () => {
      if (activeCourse && activeCourse.course) {
         callGetURLHowIO(token, activeCourse.course.courseNumber);
      }
   }

   React.useEffect(() => {
      if (urlHowIO) {
         window.open(urlHowIO, '_blank');
         resetUrlHowIO();
      }
   }, [urlHowIO]);

   const renderTrainingTable = () => {
      const currentEndorsement = endorsement.find(e => e.applicationId === parseInt(applicationId));
      // converts result to truthy/falsy value boolean strictly
      const alreadyCertified = !!(currentEndorsement && (currentEndorsement.endorsementTrainingCertifiedTimestamp || currentEndorsement.endorsementTrainingOverrideTimestamp));

      return (
         <div className="training-section">
                     <h6 className="subheader">
               Training
            </h6>
            <p className="training-section-subtitle">
               You must complete and pass the required training before you can continue with your application. You have three attempts to pass the training. If you do not pass after three attempts, a stand‑down period will apply.
            </p>
            <p className="training-section-subtitle">
               You only need to complete this training once. If you apply for another endorsement in the future, you will not need to repeat the mandatory training.
            </p>
            <div className="training-confirm-checkbox">
               <label>
                  <input
                     type="checkbox"
                     className="filled-in checkbox-blue"
                     checked={confirmedSelf}
                     onChange={(e) => setConfirmedSelf(e.target.checked)}
                     disabled={alreadyCertified}
                  />
                  <span>I confirm that I will complete this training myself. I understand that having someone else complete this training on my behalf may affect my eligibility for endorsement and/or my fitness to practise.</span>
               </label>
            </div>
            <div className="training-table-wrapper">
               <div className="training-table-header">
                  <div className="training-col training-col-course">Course</div>
                  <div className="training-col training-col-time">Est. time to complete</div>
                  <div className="training-col training-col-status">Status</div>
                  <div className="training-col training-col-action">Action</div>
               </div>
               <div className="training-table-row">
                  <div className="training-col training-col-course">Endorsement training</div>
                  <div className="training-col training-col-time">
                     <i className="material-icons training-time-icon">schedule</i>
                     <span>2 hours total</span>
                  </div>
                     { canContinue ? (      
                        <div className="training-col training-col-status training-status-passed">
                           <i className="material-icons training-pass-icon">check_circle</i>
                           <span>Pass</span>
                        </div> ) : 
                        ( <div className="training-col training-col-status training-status-pending">
                           <i className="material-icons">error</i>
                           <span>To be completed</span>
                        </div>)
                     }
                  <div className="training-col training-col-action">
                     <button
                        type="button"
                        onClick={getHowIOUrlCourse}
                        className="btn-training-complete"
                        disabled={!confirmedSelf || canContinue}
                     >
                        Complete here
                        <i className="material-icons">open_in_new</i>
                     </button>
                  </div>
               </div>
            </div>
         </div>
      );
   };

   return (
      <main>
         <Loading isLoading={isLoading} overlayOnChildren={true}>
            <div className="section no-pad-bot" id="index-banner">
               <div className="container registration-container">
                  <div className="row no-margin flex">
                     <div className="col s10 m10 l10 offset-l1">
                        <div className="row profile-row">
                           <h4 className="columnheader">{isRenewal ? 'Endorsement Renewal' : 'Endorsement Application'}</h4>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
            <StepIndicator steps={STEPS_ENDORSEMENT_FORM} currentStep={3} />
            <EndorsementTimeLimitNotice />

            <div className="section no-pad-bot" id="index-banner">
               <div className="container">
                  <div className="row">
                     <div className="col m10 l10 offset-l1">
                        {renderTrainingTable()}
                     </div>
                  </div>
                  <div className="row">
                     <div className="col m8 l8 offset-l1">
                        <div className="row profile-row next-button-registration right">
                           <div className="navigate-buttons">
                              <button
                                 type="button"
                                 className="waves-light btn btn-inactive"
                                 onClick={onBack}
                              >
                                 Back
                              </button>
                              <button
                                 type="button"
                                 className="waves-light btn btn-inactive btn-ml-30"
                                 onClick={Navigator.toEndorsements}
                              >
                                 Save and Exit
                              </button>
                              <button
                                 type="button"
                                 className="waves-effect waves-light btn btn-ml-30"
                                 onClick={onSaveAndNext}
                                 disabled={!canContinue || !confirmedSelf}
                              >
                                 Next
                              </button>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </Loading>
      </main>
   );
};

EndorsementTrainingPage.propTypes = {
   token: PropTypes.string.isRequired,
   isLoading: PropTypes.bool.isRequired,
   patchApplication: PropTypes.func.isRequired,
   getActiveEndorsementCourse: PropTypes.func.isRequired,
   activeCourse: PropTypes.object,
   urlHowIO: PropTypes.string,
   callGetURLHowIO: PropTypes.func.isRequired,
   resetUrlHowIO: PropTypes.func.isRequired,
   endorsement: PropTypes.array,
   updatePatchSuccess: PropTypes.bool.isRequired,
   isRenewal: PropTypes.bool,
};

const mapStateToProps = state => ({
   isLoading: isFetchingApplication("patchApplication",state)|| isFetchingActiveEndorsementCourse(state) || isFetchingApplication("getApplicationPayload",state) || isFetchingActiveEndorsementCourse(state) || isFetchingHowIOurlSSO(state),
   token: getToken(state),
   activeCourse: getActiveEndorsementCourseData(state),
   urlHowIO: state.course.urlHowIO,
   updatePatchSuccess: isFetchingApplicationSuccess('patchApplication', state) || false,
   endorsement: state.application.payload.applications || [],
});

const mapDispatchToProps = {
   patchApplication: ApplicationsAction.patchApplicationRequest,
   getActiveEndorsementCourse: CourseActions.getActiveEndorsementCourseRequest,
   callGetURLHowIO: CourseActions.getHowIoUrlSsoRequest,
   resetUrlHowIO: CourseActions.getHowIoUrlSsoReset,
   getApplicationPayload: ApplicationsAction.getApplicationPayloadRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(EndorsementTrainingPage);
